// See hooks.ts. Owned by the halls family of kits (docs/186 EA7): the built indoor kits and the rock kits the
// forge places (keep, cistern, crypt, sewer, brick, shrine, tomb, temple, cavern, ember; mine draws well too).
// What each kit's own board script draws beyond the shared painter (docs/166 C2): the water's built edges
// (halls-water.ts), the strips, pipes, glints and stands of halls-dress.ts, the light of halls-light.ts, and here
// the rock kits' chasms and ice, and the big flat pieces laid over a room's floor.
import { EDGE_DEPTHS } from '../../../ui/screens/map/groundEdges';
import type { Hooks } from './hooks';
import { Canvas, type RGBA } from './canvas';
import { fromOklab } from './colour';
import { C, type Ctx } from './context';
import { groundOf, isLiquid, shoreOf, type Ground } from './ground';
import { cisternKerb, fadeDeep, hasWater, shortBanks, shrineJungleKerb, shrineShelf, tankKerb, templeKerb } from './halls-water';
import { pipeRuns, restand, torchGlints, wallFeet } from './halls-dress';
import { roofShaft, windowLight } from './halls-light';

type Grounds = ReadonlyArray<Ground | undefined>;

const KITS = new Set(['keep', 'cistern', 'crypt', 'sewer', 'brick', 'shrine', 'tomb', 'temple', 'cavern', 'ember', 'mine']);
/** The temperate sets are the first, made before any of these rules: they keep the shared drawing. */
const ours = (ctx: Ctx): boolean => ctx.built && KITS.has(ctx.set.kit) && ctx.set.biome !== 'temperate';
const ROCK = new Set(['cavern', 'ember', 'tomb', 'mine']);
/** The keeps whose water wants the bank's fray at half reach (docs/166 C2). */
const SHORT_KEEPS = new Set(['desert', 'highland', 'jungle', 'underdark']);

// ---- the rock kits' chasms in their own darks (the `chasm` block of each `cavern_board.py.txt`, `ember_board.py.txt`) ----

/** The drop's dark, the far wall's hue (OKLab a, b; lightness `.2 - k * .012` down it) and the lit near rim. */
interface Drop { abyss: RGBA; wall: [number, number]; rim: [number, number, number] }
const drop = (abyss: [number, number, number], wall: [number, number], rim: [number, number, number] = [0.42, wall[0], wall[1]]): Drop => ({ abyss: [...abyss, 255], wall, rim });
const DROPS: Record<string, Drop> = {
  'cavern/ashlands': drop([18, 16, 17], [0.006, 0.002]),
  'cavern/boreal': drop([7, 9, 13], [-0.003, -0.013]),
  'cavern/desert': drop([10, 7, 6], [0.03, 0.02]),
  'cavern/highland': drop([7, 7, 11], [0.008, -0.016]),
  'cavern/jungle': drop([7, 7, 11], [0.008, -0.016]),
  'cavern/underdark': drop([7, 7, 11], [0, -0.02]),
  'cavern/wetland': drop([9, 8, 6], [-0.002, 0.02]),
  'ember/ashlands': drop([5, 5, 9], [0.014, 0.004], [0.4, 0.024, 0.018]),
  'ember/boreal': drop([10, 6, 5], [0.012, 0.01]),
  'ember/desert': drop([10, 7, 12], [0.015, -0.008]),
  'ember/highland': drop([10, 7, 12], [0.012, -0.02]),
  'ember/jungle': drop([10, 7, 12], [0.012, -0.02]),
  'ember/underdark': drop([5, 5, 9], [0.014, 0.004], [0.4, 0.024, 0.018]),
  'ember/wetland': drop([10, 7, 6], [0.012, 0.01]),
};

function paintDrops(ctx: Ctx, out: Canvas, grounds: Grounds): void {
  const D = DROPS[`${ctx.set.kit}/${ctx.set.biome}`];
  if (!D) return;
  const { sq } = ctx, { w, h } = sq;
  const chasm = (x: number, y: number): boolean => sq.at(x, y) === 'chasm' && !((sq.tile(x, y)?.elev ?? 0) > 0);
  const rim = fromOklab(...D.rim);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (!chasm(x, y)) continue;
    out.rect(x * C, y * C, x * C + C - 1, y * C + C - 1, D.abyss);
    for (const [s, dx, dy] of [['n', 0, -1], ['s', 0, 1], ['w', -1, 0], ['e', 1, 0]] as const) {
      if (!sq.on(x + dx, y + dy) || chasm(x + dx, y + dy) || sq.solid(x + dx, y + dy)) continue;
      const n = grounds[(y + dy) * w + x + dx];
      const prof = EDGE_DEPTHS[ctx.roll(`rim:${s}`, x, y) % EDGE_DEPTHS.length]!;
      for (let i = 0; i < C; i++) {
        const lip = prof[i]!, deep = lip + (s === 'n' ? 5 : 0);
        for (let k = 0; k < deep; k++) {
          const [ix, iy] = s === 'n' ? [i, k] : s === 's' ? [i, C - 1 - k] : s === 'w' ? [k, i] : [C - 1 - k, i];
          // the script counts the far wall from the rim's own top row, so it darkens all the way down
          const c: RGBA = s === 'n' && k >= lip - 1 ? fromOklab(Math.max(0.04, 0.2 - k * 0.012), D.wall[0], D.wall[1]) : s === 's' && k === lip - 1 ? rim : n ? Canvas.sample(n.piece, ix, iy, n.turn) : D.abyss;
          out.set(x * C + ix, y * C + iy, c);
        }
      }
    }
  }
}

// ---- ice with the pool's natural edge: the floor frays over it, a thin dark wet line, no rim (`BANKED`) -------------

function iceEdges(ctx: Ctx, out: Canvas, grounds: Grounds): void {
  if (!ctx.has('ice')) return;
  const { sq } = ctx, { w, h } = sq;
  const ice = (x: number, y: number): boolean => !!sq.tile(x, y)?.ice;
  const [lip, wet] = shoreOf(ctx);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (!ice(x, y)) continue;
    const land: Array<{ s: string; n?: Ground; prof?: readonly number[] }> = [];
    for (const [s, dx, dy] of [['n', 0, -1], ['s', 0, 1], ['w', -1, 0], ['e', 1, 0], ['ne', 1, -1], ['se', 1, 1], ['sw', -1, 1], ['nw', -1, -1]] as const) {
      if (!sq.on(x + dx, y + dy) || ice(x + dx, y + dy)) continue;
      const n = grounds[(y + dy) * w + x + dx];
      if (n && isLiquid(n.name)) continue;
      if (!n && (s.length > 1 || !sq.solid(x + dx, y + dy))) continue;
      land.push({ s, n, prof: s.length === 1 ? EDGE_DEPTHS[ctx.roll(`ice:${s}`, x, y) % EDGE_DEPTHS.length] : undefined });
    }
    if (!land.length) continue;
    for (let iy = 0; iy < C; iy++) for (let ix = 0; ix < C; ix++) {
      let best = Infinity, from: Ground | undefined;
      for (const l of land) {
        let kk: number, dd: number;
        if (l.prof) {
          const [i, k] = l.s === 'n' ? [ix, iy] : l.s === 's' ? [ix, C - 1 - iy] : l.s === 'w' ? [iy, ix] : [iy, C - 1 - ix];
          kk = k; dd = l.prof[i]!;
        } else { kk = Math.max(l.s.includes('w') ? ix : C - 1 - ix, l.s.includes('n') ? iy : C - 1 - iy); dd = 3; }
        if (kk - dd < best) { best = kk - dd; from = l.n; }
      }
      const X = x * C + ix, Y = y * C + iy;
      if (best < -1 && from) out.copy(X, Y, from.piece, ix, iy, from.turn);
      else if (best < 0) out.set(X, Y, lip);
      else if (best < 1) out.set(X, Y, wet);
    }
  }
}

// ---- big flat pieces over a room's floor (`big_rug`, `big_mosaic`): seeded, rare, where a block of plain floor fits ----

function bigFloors(ctx: Ctx, out: Canvas): void {
  const name = ['big_mosaic', 'big_rug'].find((p) => ctx.has(p));
  if (!name) return;
  const { sq } = ctx, { w, h } = sq;
  const p0 = ctx.all(name)[0]!;
  const bw = Math.ceil(p0.w / C), bh = Math.ceil(p0.h / C);
  const plain = (x: number, y: number): boolean => {
    const t = sq.tile(x, y);
    return sq.at(x, y) === 'floor' && !!t && !t.exit && !t.hazard && !t.objective && !t.ice && !t.trap && !(t.cover > 0) && t.terrain !== 'Door';
  };
  let open = 0;
  for (let i = 0; i < w * h; i++) if (sq.sq[i] === 'floor') open++;
  const cands: Array<[number, number, number]> = [];
  for (let y = 1; y + bh < h; y++) for (let x = 1; x + bw < w; x++) {
    // the block and a square round it are plain floor: it lies in a room, never in a doorway or a corridor
    let ok = true;
    for (let j = -1; j <= bh && ok; j++) for (let i = -1; i <= bw; i++) if (!plain(x + i, y + j)) { ok = false; break; }
    if (ok) cands.push([ctx.roll('big floor', x, y), x, y]);
  }
  cands.sort((a, b) => a[0] - b[0]);
  const limit = 1 + Math.floor(open / 900), placed: Array<[number, number]> = [];
  for (const [r, x, y] of cands) {
    if (placed.length >= limit) break;
    if (r % 3) continue;   // rare: not every board with room for one has one
    if (placed.some(([a, b]) => Math.abs(a - x) < bw + 6 && Math.abs(b - y) < bh + 6)) continue;
    placed.push([x, y]);
    const p = ctx.pick(name, 'big pick', x, y)!;
    out.draw(p, x * C + Math.floor((bw * C - p.w) / 2), y * C + Math.floor((bh * C - p.h) / 2));
  }
}

/** A lone wall square (a pillar, drawn as a stand) has no ground of its own on a built kit: the floor runs on under it, not the dark under the walls. */
function pillarFloors(ctx: Ctx, out: Canvas, grounds: Grounds): void {
  if (!ctx.has('stand')) return;
  const { sq } = ctx, { w, h } = sq;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (sq.at(x, y) !== 'wall' || sq.isWall(x - 1, y) || sq.isWall(x + 1, y) || sq.isWall(x, y - 1) || sq.isWall(x, y + 1)) continue;
    // a pillar in a pool stands in the water (two or more of its sides liquid), as `restand` gives it a wet stand
    const wet = [[1, 0], [-1, 0], [0, 1], [0, -1]].map(([dx, dy]) => grounds[(y + dy!) * w + x + dx!]?.name).filter((n): n is string => !!n && isLiquid(n));
    const name = wet.length >= 2 ? (wet.includes('water') ? 'water' : wet[0]!) : 'floor';
    const g = groundOf(ctx, name, name, x, y) ?? groundOf(ctx, 'floor', 'floor', x, y);
    if (g) out.draw(g.piece, x * C, y * C, g.turn, true);
  }
}

/** Decals each kit lays by its own rule, never from the scatter: ferns only under a gap in the roof (the jungle crypt and the sewers), the underdark shrine's caps only on a pool's lip. */
const OWN_DECALS: Record<string, RegExp> = { crypt: /^decal_fern$/, sewer: /^decal_fern$/, shrine: /^decal_caps$/ };

export const hooks: Hooks = {
  ownsDecal(ctx, decal) {
    if (!ours(ctx)) return false;
    if (ctx.set.kit === 'shrine' && ctx.set.biome !== 'underdark') return false;
    return OWN_DECALS[ctx.set.kit]?.test(decal) ?? false;
  },

  banks(ctx, out, grounds) {
    if (!ours(ctx) || !hasWater(ctx, grounds)) return false;
    const { kit, biome } = ctx.set;
    if (kit === 'cistern') {
      fadeDeep(ctx, out, grounds, biome === 'boreal' ? undefined : biome === 'ashlands' ? 'wide' : 'even');
      cisternKerb(ctx, out, grounds);
      return true;
    }
    if (ctx.look['tank'] && (kit === 'keep' || kit === 'brick' || kit === 'temple' || kit === 'shrine')) { tankKerb(ctx, out, grounds, kit === 'shrine'); return true; }
    if (kit === 'keep' && SHORT_KEEPS.has(biome)) { shortBanks(ctx, out, grounds); return true; }
    if (kit === 'temple') { templeKerb(ctx, out, grounds); return true; }
    if (kit === 'shrine') {
      fadeDeep(ctx, out, grounds, 'even');
      if (biome === 'jungle') shrineJungleKerb(ctx, out, grounds); else shrineShelf(ctx, out, grounds);
      return true;
    }
    return false;
  },

  afterGround(ctx, out, grounds) {
    if (!ours(ctx)) return;
    pillarFloors(ctx, out, grounds);
    if (ctx.set.kit === 'cavern' || ctx.set.kit === 'ember') paintDrops(ctx, out, grounds);
  },

  afterFlat(ctx, out, grounds) {
    if (!ours(ctx)) return;
    if (ROCK.has(ctx.set.kit)) iceEdges(ctx, out, grounds);
    if (ctx.set.kit === 'keep' || ctx.set.kit === 'crypt' || ctx.set.kit === 'shrine' || ctx.set.kit === 'temple') bigFloors(ctx, out);
  },

  afterStanding(ctx, out, grounds, standing) {
    if (!ours(ctx)) return;
    wallFeet(ctx, out, grounds);
    torchGlints(ctx, out, grounds);
    restand(ctx, out, grounds, standing);
    pipeRuns(ctx, standing);
    roofShaft(ctx, out, grounds, standing);
    windowLight(ctx, out, grounds, standing);
  },
};
